import keys from 'lodash/keys';

import { Session } from 'types/session';

/**
 * Utility functions to handle sessionStorage with expiration support
 */
const sessionCache = {
	set(key: string, value: any, expirationMinutes?: number) {
		const item = {
			value: value,
			expiry: expirationMinutes ? Date.now() + expirationMinutes * 60 * 1000 : null
		};
		sessionStorage.setItem(key, JSON.stringify(item));
	},
	get(key: string): any {
		const itemStr = sessionStorage.getItem(key);
		if (!itemStr) return null;

		try {
			const item = JSON.parse(itemStr);
			if (item.expiry && Date.now() > item.expiry) {
				this.remove(key);
				return null;
			}
			return item.value;
		} catch {
			this.remove(key);
			return null;
		}
	},
	remove(key: string) {
		sessionStorage.removeItem(key);
	}
};

/**
 * Expirations in minutes
 */
const expirations = {
	facebookSkip: 60 * 24 * 3 // 3 days
} as const;

export const facebookStorage = {
	getSkipped: (): boolean => {
		const skipped = sessionCache.get('facebook_skipped'); // null if not set or expired
		return skipped === 'true';
	},
	setSkipped: (skipped: boolean) =>
		sessionCache.set('facebook_skipped', skipped ? 'true' : 'false', expirations.facebookSkip)
};

let lastToken: string | null = null;
export const terStorage = {
	getToken: (): string | null => {
		const token = sessionCache.get('api_token');
		if (lastToken && token && lastToken.split('-')[0] !== token.split('-')[0]) {
			terStorage.clear(); // prevent requests while reloading
			// eslint-disable-next-line no-restricted-globals
			location.reload(); // force reload if token changes
		}
		return token;
	},
	setToken: (token: string) => {
		lastToken = token;
		sessionCache.set('api_token', token); // no expiration
	},
	getTokenExpiresAt: (): string | null => sessionCache.get('api_tokenExpiresAt'),
	setTokenExpiresAt: (tokenExpiresAt: string) => sessionCache.set('api_tokenExpiresAt', tokenExpiresAt),
	clear: () => {
		sessionCache.remove('api_token');
		sessionCache.remove('api_tokenExpiresAt');
	}
};

export const JWTStorage = {
	getToken: (app: string): string | null => sessionCache.get(`jwt_${app}`),
	setToken: (jwt: Session['jwt']) =>
		keys(jwt).forEach(app =>
			sessionCache.set(`jwt_${app}`, jwt[app].access_token)
		)
};
